import { useEffect, FC, useState } from "react";
import { Button } from "src/components/ui/button";
import PreviousObservation from "./PreviousObservation";
import usePreviousObservationFormQuery from "src/hooks/useObservationForm/useFilterFormInstance/useFilterFormInstance";
import formatDate from "src/utils/formatDate";
import isObservationCompleted from "./isObservationCompleted";
import AlertModal from "src/modals/AlertModal/AlertModal";
import { HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { AxiosError } from "axios";
import dayjs from "dayjs";

interface Props {
	formId: number;
	userId?: number;
	storeId?: number;
	onResetSelectedSearchItem?: () => void;
	observationType: IObservationType;
}

const PreviousObservations: FC<Props> = ({
	formId,
	userId,
	storeId,
	observationType,
	onResetSelectedSearchItem
}) => {
	const { data, isLoading, isFetching, isError, error } =
		usePreviousObservationFormQuery({
			formId,
			storeId,
			userId
		});

	const [observationId, setObservationId] = useState<number>();
	const [isUnfinishedFormModalOpen, setIsUnfinishedFormModalOpen] =
		useState(false);

	useEffect(() => {
		if (!data || isFetching) return;

		const filter = data.filter(
			(observation) =>
				isObservationCompleted(observation, observationType).outcome ===
				"Not Completed (In Progress)"
		);

		if (filter.length !== 0) {
			setObservationId(filter[0].id);
		}
	}, [data, isFetching]);

	const handleCancelButtonClick = () => {
		if (!onResetSelectedSearchItem) return;

		onResetSelectedSearchItem();
	};

	if (isLoading || isFetching) {
		return (
			<div className="min-h-[300px]">
				<div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-foreground" />
			</div>
		);
	}

	if (data && data.length === 0) {
		return (
			<>
				<div className="min-h-[300px]" data-testid="no-data-msg">
					No previous observations
				</div>

				<div
					className={`flex items-center my-10 ${onResetSelectedSearchItem ? "justify-between" : "justify-end"}`}
				>
					{onResetSelectedSearchItem && (
						<Button variant="outline" onClick={handleCancelButtonClick}>
							Cancel
						</Button>
					)}

					<Button asChild>
						<Link
							to={
								userId
									? `/observations/submissions/${formId}/user/${userId}/create-observation`
									: `/observations/submissions/${formId}/store/${storeId}/create-observation`
							}
						>
							Begin new observation
						</Link>
					</Button>
				</div>
			</>
		);
	}

	if (isError && error)
		return <p className="text-red-500">{(error as AxiosError).message}</p>;

	return (
		<>
			<div className="bg-white rounded-xl border border-border-light shadow-sm p-6 mb-2">
				<p className="font-heading font-bold">Previous observations</p>
				<div className="mt-4">
					<hr className="border-border" />
				</div>

				{data?.map((observation, index) => {
					const { outcome, isFinished } = isObservationCompleted(
						observation,
						observationType
					);

					return (
						<PreviousObservation
							key={index}
							completedAt={formatDate(observation.completed_at)}
							updatedAt={formatDate(observation.updated_at)}
							userId={userId}
							observedAt={
								observation.year && observation.month
									? dayjs(
											new Date(observation.year, observation.month, 0)
											// eslint-disable-next-line no-mixed-spaces-and-tabs
										).format("MMM YY")
									: undefined
							}
							outcome={outcome}
							store={observation.store}
							isFinished={isFinished}
							borderTop={index === 0 ? "0px" : "1px"}
							opacity={outcome === "Not Completed (Expired)" ? 0.4 : 1}
							formId={formId}
							observationId={observation.id}
						/>
					);
				})}
			</div>

			<div
				className={`flex items-center my-10 ${onResetSelectedSearchItem ? "justify-between" : "justify-end"}`}
			>
				{onResetSelectedSearchItem && (
					<Button variant="outline" onClick={handleCancelButtonClick}>
						Cancel
					</Button>
				)}

				{observationId && (
					<Button onClick={() => setIsUnfinishedFormModalOpen(true)}>
						Begin new observation
					</Button>
				)}

				{!observationId && (
					<Button asChild>
						<Link
							to={
								userId
									? `/observations/submissions/${formId}/user/${userId}/create-observation`
									: `/observations/submissions/${formId}/store/${storeId}/create-observation`
							}
						>
							Begin new observation
						</Link>
					</Button>
				)}
			</div>

			{/* Unfinished Form Modal */}
			{observationId && (
				<AlertModal
					onClose={() => setIsUnfinishedFormModalOpen(false)}
					isOpen={isUnfinishedFormModalOpen}
					ModalControllers={
						<>
							<Button
								variant="outline"
								size="lg"
								onClick={() => setIsUnfinishedFormModalOpen(false)}
							>
								Cancel
							</Button>

							<Button size="lg" asChild>
								<Link
									to={
										userId
											? `/observations/submissions/${formId}/user/${userId}/create-observation`
											: `/observations/submissions/${formId}/store/${storeId}/create-observation`
									}
								>
									Begin new observation
								</Link>
							</Button>
						</>
					}
				>
					<HelpCircle size={48} data-testid="help-icon" />
					<div className="mt-4">
						<p className="font-bold">You have an unfinished observation!</p>
						<p className="text-sm text-foreground/60">
							Starting a new one now will replace this observation.
						</p>
					</div>
				</AlertModal>
			)}
		</>
	);
};

export default PreviousObservations;
